import { Page } from "@/types/pagination";
import {
  TaskCommentDetail,
  type TaskCommentUpdate,
  type TaskCounts,
  TaskCreate,
  TaskDetail,
  TaskFetchParams,
  type TaskLinkedCounts,
  TaskLogDetail,
  TaskSummary,
  TaskUpdate,
} from "@/types/task";

import { requestData } from "@/api/api-config";
import { serializeFilterRequest } from "@/features/table-filters/contract/state";

export const EMPTY_TASK_COUNTS: TaskCounts = {
  total: 0,
  linked: { call: 0 },
};

type TaskCountsResponse =
  | number
  | (Partial<Omit<TaskCounts, "linked">> & {
      linked?: Partial<TaskLinkedCounts>;
    })
  | null;

function normalizeTaskCounts(result: TaskCountsResponse): TaskCounts {
  if (typeof result === "number") {
    return {
      total: result,
      linked: { call: 0 },
    };
  }

  return {
    total: result?.total ?? 0,
    linked: {
      call: result?.linked?.call ?? 0,
    },
  };
}

export async function fetchTaskCounts(params?: {
  assigneeId?: string;
  subscriberId?: string;
  /** Count everything rather than falling back to the caller's default scope. */
  allTasks?: boolean;
  /** Scopes only `linked.call` to calls at these locations. */
  locationIds?: string[];
}): Promise<TaskCounts> {
  const result = await requestData<TaskCountsResponse>("task/count", "get", {
    searchParams: params,
  });
  return normalizeTaskCounts(result);
}

export async function updateTaskDefaultFilter(value: string): Promise<boolean> {
  const result = await requestData<boolean>(
    `admin/settings/task-default-filter/${value}`,
    "put",
    undefined,
    false,
  );
  return result ?? false;
}

export async function fetchTasks(
  params?: TaskFetchParams,
): Promise<Page<TaskSummary> | null> {
  return await requestData<Page<TaskSummary>>("task/list", "get", {
    searchParams: params ? serializeFilterRequest(params) : undefined,
  });
}

export async function fetchTasksExist(): Promise<boolean> {
  const result = await requestData<{ exists: boolean }>("task/exists", "get");
  return result?.exists ?? false;
}

export async function fetchTask(taskId: string): Promise<TaskDetail | null> {
  return await requestData<TaskDetail>(`task/${taskId}`, "get");
}

export async function createTask(
  task: TaskCreate,
): Promise<TaskSummary | null> {
  return await requestData<TaskSummary>(
    "task",
    "post",
    {
      json: task,
    },
    false,
  );
}

export async function updateTask(
  taskId: string,
  task: TaskUpdate,
): Promise<TaskSummary | null> {
  return await requestData<TaskSummary>(
    `task/${taskId}`,
    "put",
    {
      json: task,
    },
    false,
  );
}

export async function deleteTask(taskId: string): Promise<void> {
  await requestData(`task/${taskId}`, "delete", undefined, false);
}

export async function completeTask(
  taskId: string,
): Promise<TaskSummary | null> {
  return await requestData<TaskSummary>(
    `task/${taskId}/complete`,
    "post",
    undefined,
    false,
  );
}

export async function addComment(
  taskId: string,
  content: string,
  files: File[] = [],
): Promise<TaskCommentDetail | null> {
  const formData = new FormData();
  formData.append("content", content);
  files.forEach((file) => formData.append("files", file));
  return await requestData<TaskCommentDetail>(
    `task/${taskId}/comment`,
    "post",
    { body: formData },
    false,
    60000,
  );
}

export async function fetchComments(
  taskId: string,
): Promise<TaskCommentDetail[] | null> {
  return await requestData<TaskCommentDetail[]>(
    `task/${taskId}/comment`,
    "get",
  );
}

export async function fetchTaskActivity(
  taskId: string,
): Promise<TaskLogDetail[] | null> {
  return await requestData<TaskLogDetail[]>(`task/${taskId}/activity`, "get");
}

export async function setTaskSubscribers(
  taskId: string,
  providerIds: string[],
): Promise<TaskDetail | null> {
  return await requestData<TaskDetail>(
    `task/${taskId}/subscribers`,
    "put",
    {
      json: { providerIds },
    },
    false,
  );
}

export async function subscribeToTask(taskId: string): Promise<void> {
  await requestData(`task/${taskId}/subscribe`, "post", undefined, false);
}

export async function unsubscribeFromTask(taskId: string): Promise<void> {
  await requestData(`task/${taskId}/subscribe`, "delete", undefined, false);
}

export async function updateComment(
  taskId: string,
  commentId: string,
  comment: TaskCommentUpdate,
): Promise<TaskCommentDetail | null> {
  return await requestData<TaskCommentDetail>(
    `task/${taskId}/comment/${commentId}`,
    "put",
    { json: comment },
    false,
  );
}

export async function deleteComment(
  taskId: string,
  commentId: string,
): Promise<void> {
  await requestData(
    `task/${taskId}/comment/${commentId}`,
    "delete",
    undefined,
    false,
  );
}
